import React, { useState } from 'react';
import { 
  Clock, 
  UserX, 
  ShieldAlert, 
  RefreshCw, 
  LogOut, 
  ShieldCheck, 
  Lock, 
  CheckCircle2,
  Sparkles,
  Info
} from 'lucide-react';
import { UserProfile } from '../types';
import { getUserProfile } from '../lib/firebase';

interface UserStatusGateProps {
  user: UserProfile;
  onRefresh: (updatedUser: UserProfile) => void;
  onLogout: () => void;
  onOpenAdmin: () => void;
}

export const UserStatusGate: React.FC<UserStatusGateProps> = ({
  user,
  onRefresh,
  onLogout,
  onOpenAdmin
}) => {
  const [isChecking, setIsChecking] = useState(false);
  const [checkResult, setCheckResult] = useState<string | null>(null);

  const isPending = user.status === 'pending';
  const isBlocked = user.status === 'blocked';

  const handleCheckStatus = async () => {
    setIsChecking(true);
    setCheckResult(null);
    try {
      const updated = await getUserProfile(user.uid);
      if (updated) {
        onRefresh(updated);
        if (updated.status === 'active') {
          setCheckResult('validat');
        } else if (updated.status === 'pending') {
          setCheckResult('pendent');
        } else if (updated.status === 'blocked') {
          setCheckResult('bloquejat');
        }
      }
    } catch (err: any) {
      setCheckResult('error');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#070b14] text-slate-100 flex items-center justify-center p-4 selection:bg-sky-500 selection:text-black">
      
      {/* Background Ambient Glows */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
        <div className={`absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full blur-[120px] opacity-20 ${
          isPending ? 'bg-sky-500' : 'bg-red-500'
        }`} />
      </div>

      <div className="w-full max-w-md bg-[#0c1220] border border-white/10 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-6 text-center animate-in fade-in zoom-in-95 duration-200">
        
        {/* Animated Status Icon */}
        <div className="relative mx-auto w-20 h-20 flex items-center justify-center">
          {isPending ? (
            <>
              <div className="absolute inset-0 rounded-3xl bg-sky-500/20 animate-ping opacity-30" />
              <div className="relative w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-600/30 to-cyan-500/10 border border-sky-500/40 text-sky-400 flex items-center justify-center shadow-lg shadow-sky-500/20">
                <Clock className="w-10 h-10" />
              </div>
            </>
          ) : (
            <div className="relative w-20 h-20 rounded-3xl bg-gradient-to-br from-red-500/30 to-rose-500/10 border border-red-500/40 text-red-400 flex items-center justify-center shadow-lg shadow-red-500/20">
              <UserX className="w-10 h-10" />
            </div>
          )}
        </div>

        {/* Header Text */}
        <div className="space-y-2">
          <h2 className="font-display font-black text-2xl text-white">
            {isPending ? 'Compte Pendent de Validació' : 'Compte Bloquejat o Desactivat'}
          </h2>
          <p className="text-xs text-slate-300 leading-relaxed">
            {isPending ? (
              <>
                Hola, <span className="text-sky-400 font-bold">{user.displayName || user.username}</span>! El teu compte s'ha registrat correctament, però requereix que un <span className="text-white font-semibold">administrador el validi i activi</span> abans de poder accedir a les col·leccions.
              </>
            ) : (
              <>
                Aquest compte d'usuari (<span className="text-red-400 font-semibold">@{user.username}</span>) ha estat desactivat per l'administració. Contacta amb l'administrador del sistema per a més informació.
              </>
            )}
          </p>
        </div>

        {/* User Card Info */}
        <div className="p-3.5 rounded-2xl bg-white/5 border border-white/5 flex items-center justify-between text-xs">
          <div className="flex items-center gap-3">
            <img 
              src={user.avatar || 'https://images.unsplash.com/photo-1534447677768-be436bb09401?w=150'} 
              alt={user.username} 
              className="w-10 h-10 rounded-full border border-sky-400/50 object-cover"
              referrerPolicy="no-referrer"
            />
            <div className="text-left">
              <p className="font-bold text-white leading-tight">{user.displayName || user.username}</p>
              <p className="text-[11px] text-slate-400 font-mono">@{user.username}</p>
            </div>
          </div>
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${
            isPending ? 'bg-sky-500/20 text-sky-400 border border-sky-500/30' : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {isPending ? 'Pendent' : 'Bloquejat'}
          </span>
        </div>

        {/* Status feedback message */}
        {checkResult === 'pendent' && (
          <p className="text-xs text-sky-300 animate-in fade-in">
            El compte encara està pendent d'aprovació per part de l'administrador.
          </p>
        )}
        {checkResult === 'bloquejat' && (
          <p className="text-xs text-red-400 animate-in fade-in">
            El compte continua bloquejat.
          </p>
        )}

        {/* Actions */}
        <div className="space-y-3 pt-2">
          <button
            onClick={handleCheckStatus}
            disabled={isChecking}
            className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-white font-bold text-xs sm:text-sm shadow-lg shadow-sky-500/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50 cursor-pointer"
          >
            <RefreshCw className={`w-4 h-4 ${isChecking ? 'animate-spin' : ''}`} />
            <span>{isChecking ? 'Comprovant estat...' : 'Comprovar si ja m\'han activat'}</span>
          </button>

          <div className="flex items-center justify-between gap-3 pt-1">
            <button
              onClick={onLogout}
              className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 text-xs font-semibold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Tancar Sessió</span>
            </button>

            <button
              onClick={onOpenAdmin}
              className="flex-1 py-2.5 rounded-xl bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 border border-sky-500/20 text-xs font-semibold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>Panell de Gestió</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
